import { Link } from "@inertiajs/react"
import { ChevronRight, TriangleAlert } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { NAV, MEMBER_NAV, type NavCountKey, type NavItemSpec, type NavRouteKey, type NavSectionSpec } from "./nav"

export type NavRoutes = Partial<Record<NavRouteKey, string | null>> & { home?: string }
export type NavCounts = Partial<Record<NavCountKey, number>>

type ResolvedItem = NavItemSpec & { href: string; count: number | null; urgent: boolean }
type ResolvedSection = { id: string; label: string | null; items: ResolvedItem[]; total: number }

// Which nav to draw is the server's call (`auth.user.nav`): a team member with
// any granted module gets the admin nav, everyone else gets the member one
// (Documents / Forms & Resources). Deliberately not sniffed from `role` out
// here — `role` says how you sign in, not what you can reach. The route-gating
// in each spec does the rest.
function navFor(nav: string | undefined): NavSectionSpec[] {
  return nav === "admin" ? NAV : MEMBER_NAV
}

export function resolveNav(nav: NavSectionSpec[], routes: NavRoutes, counts: NavCounts): ResolvedSection[] {
  return nav.map((section) => {
    const items = section.items.flatMap<ResolvedItem>((item) => {
      const href = routes[item.route]
      if (!href) return [] // Not reachable for this user — or not built yet.

      const count = item.countKey ? (counts[item.countKey] ?? 0) : 0
      return [{ ...item, href, count: count > 0 ? count : null, urgent: item.countKey === "alerts" }]
    })

    return {
      id: section.id,
      label: section.label,
      items,
      // A folded section still shows what's waiting inside it — tidying the nav
      // must never hide the thing the nav exists to surface.
      total: items.reduce((sum, item) => sum + (item.count ?? 0), 0),
    }
  }).filter((section) => section.items.length > 0)
}

// `/drivers` should light up on `/drivers/12`, but `/` must not light up on
// everything.
export function isActive(currentPath: string, href: string): boolean {
  const path = currentPath.split("?")[0]
  if (href === "/") return path === "/"
  return path === href || path.startsWith(`${href}/`)
}

export function AdminSidebar({
  routes,
  counts,
  currentPath,
  nav,
  footer,
}: {
  routes: NavRoutes
  counts: NavCounts
  currentPath: string
  /** "admin" | "member" — from `auth.user.nav`. */
  nav?: string
  footer?: React.ReactNode
}) {
  const sections = resolveNav(navFor(nav), routes, counts)

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader className="gap-0 px-4 py-4">
        {/* A driver's "home" is their hub, not the marketing site — the logo
            has to land where the rest of their nav lives. */}
        <Link
          href={routes.driver_home ?? routes.home ?? "/"}
          className="rounded font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          FishingCreek
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {sections.map((section) =>
          section.label ? (
            <CollapsibleSection key={section.id} section={section} currentPath={currentPath} />
          ) : (
            <SidebarGroup key={section.id} className="py-1">
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <ItemRow key={item.route} item={item} currentPath={currentPath} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ),
        )}
      </SidebarContent>

      {footer && <SidebarFooter className="px-4 py-3">{footer}</SidebarFooter>}
    </Sidebar>
  )
}

function CollapsibleSection({
  section,
  currentPath,
}: {
  section: ResolvedSection
  currentPath: string
}) {
  // Every section is open by default (including the one you're in) but can always
  // be collapsed — a header that silently ignored clicks read as broken.
  return (
    <Collapsible defaultOpen className="group/section py-1" key={section.id}>
      <SidebarGroup className="py-0">
        <CollapsibleTrigger
          className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 shrink-0 text-sidebar-foreground/80 transition-transform motion-reduce:transition-none",
              "group-data-[state=open]/section:rotate-90",
            )}
            aria-hidden="true"
          />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-sidebar-foreground">
            {section.label}
          </span>
          {/* Rolled up only while folded — so tidying never costs you the signal,
              and it doesn't double up with the rows when they're visible. */}
          {section.total > 0 && (
            <span className="ml-auto font-mono text-xs tabular-nums text-sidebar-foreground group-data-[state=open]/section:hidden">
              {section.total}
            </span>
          )}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarGroupContent className="pt-1">
            <SidebarMenu>
              {section.items.map((item) => (
                <ItemRow key={item.route} item={item} currentPath={currentPath} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  )
}

function ItemRow({ item, currentPath }: { item: ResolvedItem; currentPath: string }) {
  const Icon = item.icon
  const active = isActive(currentPath, item.href)
  // A mounted engine isn't an Inertia page: `<Link>` would ask it for a page
  // object and choke on the HTML it gets back.
  const Row = item.external ? "a" : Link

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={item.label}
        // Hover must not borrow the active pill, or hovering a row would look
        // exactly like selecting it. A wash of the text colour reads as "you're
        // about to" without claiming "you are here".
        className="hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground data-[active=true]:hover:bg-sidebar-accent data-[active=true]:hover:text-sidebar-accent-foreground"
      >
        <Row href={item.href} aria-current={active ? "page" : undefined}>
          {/* No colour override: the icon inherits the row. On the active pill
              that's the accent; anywhere else it's the sidebar foreground. */}
          <Icon aria-hidden="true" />
          <span>{item.label}</span>
        </Row>
      </SidebarMenuButton>

      {item.count !== null && (
        <SidebarMenuBadge
          className={cn(
            "gap-1 font-mono tabular-nums",
            // NOT text-destructive: red on the accent surface measures 1.11:1,
            // i.e. invisible. Urgency is carried by an icon and full-strength
            // text instead — which also satisfies "never colour alone" (§7)
            // rather than leaning on a hue that a tenant accent can swallow.
            item.urgent ? "font-semibold text-sidebar-foreground" : "text-sidebar-foreground",
          )}
        >
          {item.urgent && <TriangleAlert className="h-3 w-3" aria-hidden="true" />}
          {item.count}
          <span className="sr-only">
            {item.urgent ? " needing urgent attention" : " outstanding"}
          </span>
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  )
}
